import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const SYMBOLS = { EUR: "\u20ac", USD: "$", TRY: "\u20ba" };

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [currency, setCurrency] = useState(localStorage.getItem("currency") || "EUR");
  const [rates, setRates] = useState({ EUR: 1, USD: 1.08, TRY: 37.2 });

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setAuthLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    api.get("/currency/rates").then((r) => setRates(r.data.rates)).catch(() => {});
  }, []);

  const changeCurrency = (c) => {
    setCurrency(c);
    localStorage.setItem("currency", c);
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };

  const formatPrice = (eur, opts = {}) => {
    if (eur == null || isNaN(eur)) return "\u2014";
    const val = eur * (rates[currency] || 1);
    const rounded = opts.decimals === false ? Math.round(val) : val;
    return `${SYMBOLS[currency]}${rounded.toLocaleString("en-US", {
      minimumFractionDigits: opts.decimals === false ? 0 : 0,
      maximumFractionDigits: 0,
    })}`;
  };

  return (
    <AppContext.Provider
      value={{ user, setUser, authLoading, checkAuth, logout, currency, changeCurrency, rates, formatPrice, symbol: SYMBOLS[currency] }}
    >
      {children}
    </AppContext.Provider>
  );
}
